// Comma-separated email chips — paste a list or type an address + Enter.

import React, { useMemo, useRef, useState } from "react";
import { X, CheckCircle2, AlertCircle } from "lucide-react";
import { isValidEmail, parseEmailList } from "../utils/emailComposeUtils";
import "./EmailChipInput.css";

function Chip({ email, onRemove }) {
  return (
    <span className="crm-email-chip-input__chip">
      <span className="crm-email-chip-input__chip-label">{email}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${email}`}
        className="crm-email-chip-input__chip-remove"
      >
        <X size={12} />
      </button>
    </span>
  );
}

function getDraftStatus(draft, commitAttempted) {
  const trimmed = draft.trim();
  if (!trimmed) return "idle";

  if (isValidEmail(trimmed)) return "valid";

  if (commitAttempted || trimmed.includes("@")) return "invalid";

  return "typing";
}

export default function EmailChipInput({ value = [], onChange, placeholder, id, required, onDraftChange: onDraftChangeProp }) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [commitAttempted, setCommitAttempted] = useState(false);
  const inputRef = useRef(null);

  const draftStatus = useMemo(
    () => getDraftStatus(draft, commitAttempted),
    [draft, commitAttempted],
  );

  const addEmails = (raw) => {
    const incoming = parseEmailList(raw);
    if (!incoming.length) return false;
    const next = [...value];
    for (const e of incoming) {
      const lower = e.toLowerCase();
      if (!next.some((x) => x.toLowerCase() === lower)) next.push(e);
    }
    onChange(next);
    return true;
  };

  const setDraftAndNotify = (next) => {
    setDraft(next);
    onDraftChangeProp?.(next);
  };

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return true;

    if (!isValidEmail(trimmed)) {
      setCommitAttempted(true);
      return false;
    }

    addEmails(trimmed);
    setDraftAndNotify("");
    setCommitAttempted(false);
    return true;
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commitDraft();
    } else if (e.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!text.trim()) return;
    // Chip anything pasteable — a lone "name@company.com" is just as much a
    // finished address as a delimited list, and leaving it in the draft means
    // a paste-then-Send silently drops the recipient.
    if (!parseEmailList(text).length) return;
    e.preventDefault();
    addEmails(text);
    setDraftAndNotify("");
    setCommitAttempted(false);
  };

  const onBlur = () => {
    setFocused(false);
    if (draft.trim()) commitDraft();
  };

  const onDraftChange = (e) => {
    setDraftAndNotify(e.target.value);
    setCommitAttempted(false);
  };

  const boxClass = [
    "crm-email-chip-input__box",
    focused && draftStatus === "idle" ? "is-focused" : "",
    draftStatus === "valid" ? "is-valid-draft" : "",
    draftStatus === "invalid" ? "is-invalid-draft" : "",
  ].filter(Boolean).join(" ");

  const showValidHint = focused && draftStatus === "valid";
  const showInvalidHint = draftStatus === "invalid";

  return (
    <div className="crm-email-chip-input">
      <div
        role="textbox"
        tabIndex={0}
        className={boxClass}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email, i) => (
          <Chip
            key={`${email}-${i}`}
            email={email}
            onRemove={() => onChange(value.filter((_, j) => j !== i))}
          />
        ))}

        <input
          ref={inputRef}
          id={id}
          type="text"
          value={draft}
          onChange={onDraftChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={() => setFocused(true)}
          onBlur={onBlur}
          placeholder={value.length ? "" : placeholder}
          required={required && !value.length}
          className="crm-email-chip-input__field"
          aria-invalid={draftStatus === "invalid"}
          aria-describedby={(showValidHint || showInvalidHint) ? `${id || "email"}-hint` : undefined}
        />
      </div>

      {showValidHint && (
        <div id={`${id || "email"}-hint`} className="crm-email-chip-input__hint crm-email-chip-input__hint--valid">
          <CheckCircle2 size={12} />
          <span>
            Press <kbd>Enter</kbd> or click outside to add
          </span>
        </div>
      )}

      {showInvalidHint && (
        <div id={`${id || "email"}-hint`} className="crm-email-chip-input__hint crm-email-chip-input__hint--invalid">
          <AlertCircle size={12} />
          <span>Enter a valid email address (e.g. name@company.com)</span>
        </div>
      )}
    </div>
  );
}
